import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../../../app/hooks";
import {Button, ButtonState} from "../../../button/Button";
import {CloseImageButton} from "../../../closeImageButton/CloseImageButton";
import {CustomInputField} from "../../../customInputField/CustomInputField";
import {LoadingModal} from "../../../loadingModal/LoadingModal";
import {RequestStatus} from "../../../../../api/apiConfiguration";
import {
    cloneAssistant,
    cloneErrorSelector,
    cloneRequestStatusSelector,
    resetCloneState
} from "../../../../modules/assistant/transfer/assistantTransferSlice";
import {
    AssistantCloneResult,
    EmailCloneStrategy
} from "../../../../modules/assistant/transfer/assistantTransferApi";
import {
    showSuccessToastNotification,
    showInfoToastNotification
} from "../../../toastNotification/toastNotificationSlice";
import {RadioOption} from "./RadioOption";

import styles from "./CloneAssistantModal.module.css";

const DEFAULT_NAME_PREFIX = "Copy of";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CloneAssistantModalProps {
    publicId: string
    assistantName?: string
    handleClose: (value?: any) => any
}

/**
 * Creates a copy of an assistant.
 *
 * The two choices interact: replacing emails with a placeholder only makes sense when the
 * franchises and locations are being copied, because reusing them means editing addresses that
 * other assistants also rely on.
 *
 * Uses local state rather than the shared ValidatedForm, which is built around a Redux-backed draft
 * of a persisted entity; these three fields exist only while the dialog is open.
 */
export const CloneAssistantModal: React.FC<CloneAssistantModalProps> = ({
                                                                            publicId,
                                                                            assistantName,
                                                                            handleClose
                                                                        }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [namePrefix, setNamePrefix] = useState(DEFAULT_NAME_PREFIX);
    // Reusing the existing franchises and locations is the safe default: it creates no new rows
    // and duplicates no vehicle data, so an accidental confirm cannot leave stray records behind.
    const [copyHierarchy, setCopyHierarchy] = useState(false);
    const [emailStrategy, setEmailStrategy] = useState<EmailCloneStrategy>("KEEP");
    const [placeholderEmail, setPlaceholderEmail] = useState("");
    // The confirm button stays disabled until the form is valid, so an error can only be useful if
    // it appears as the operator types rather than on a submit that cannot happen.
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

    const cloneRequestStatus = useAppSelector(cloneRequestStatusSelector);
    const cloneError = useAppSelector(cloneErrorSelector);
    const isCloning = cloneRequestStatus === RequestStatus.PENDING;

    useEffect(() => {
        dispatch(resetCloneState());
    }, [dispatch]);

    const needsPlaceholderEmail = copyHierarchy && emailStrategy === "PLACEHOLDER";
    const namePrefixError = !namePrefix.trim() ? "A name prefix is required" : undefined;
    const placeholderEmailError = !needsPlaceholderEmail
        ? undefined
        : !placeholderEmail.trim()
            ? "A placeholder email address is required"
            : !EMAIL_PATTERN.test(placeholderEmail.trim())
                ? "Enter a valid email address"
                : undefined;

    const isValid = !namePrefixError && !placeholderEmailError;

    const markTouched = (field: string) => setTouchedFields(previous => new Set(previous).add(field));

    const handleClone = async () => {
        if (!isValid) {
            return;
        }

        try {
            const result: AssistantCloneResult = await dispatch(cloneAssistant({
                publicBotId: publicId,
                request: {
                    namePrefix: namePrefix.trim(),
                    copyFranchisesAndLocations: copyHierarchy,
                    emailStrategy: copyHierarchy ? emailStrategy : undefined,
                    placeholderEmail: needsPlaceholderEmail ? placeholderEmail.trim() : undefined
                }
            })).unwrap();

            handleClose();
            if (result.vehicleCloneFailed) {
                dispatch(showInfoToastNotification({
                    message: `${result.assistantName} was created, but its vehicle data could not be copied. `
                        + "Use Reimport vehicles to fill it in."
                }));
            } else {
                dispatch(showSuccessToastNotification({message: `${result.assistantName} created`}));
            }
            navigate(`/assistants/${result.assistantPublicId}`);
        } catch {
            // The message is held in the store and shown in the dialog, which stays open.
        }
    };

    if (isCloning) {
        return <LoadingModal
            title={"Cloning assistant"}
            subtitle={"Copying the configuration and, where needed, the vehicle data."}
        />;
    }

    return <div className={styles.wrapper}>
        <div className={styles.titleWrapper}>
            <span className={styles.title}>Clone assistant</span>
            <CloseImageButton onClose={handleClose}/>
        </div>
        <span className={styles.subtitle}>
            Creates a copy of {assistantName ? `"${assistantName}"` : "this assistant"} with the same
            settings, tools, appearance and offers. Conversations are not copied.
        </span>

        <fieldset className={styles.group}>
            <legend className={styles.groupTitle}>Franchises and locations</legend>
            <RadioOption
                name={"hierarchy"}
                checked={!copyHierarchy}
                onSelect={() => setCopyHierarchy(false)}
                label={"Use the existing ones"}
                description={"The clone shares the current franchises and locations. Nothing new is created."}
            />
            <RadioOption
                name={"hierarchy"}
                checked={copyHierarchy}
                onSelect={() => setCopyHierarchy(true)}
                label={"Copy them"}
                description={"Creates new franchises and locations, and duplicates the existing vehicle data."}
            />
        </fieldset>

        {copyHierarchy && <fieldset className={styles.group}>
            <legend className={styles.groupTitle}>Email addresses</legend>
            <RadioOption
                name={"emails"}
                checked={emailStrategy === "KEEP"}
                onSelect={() => setEmailStrategy("KEEP")}
                label={"Keep the existing addresses"}
                description={"The copies email the same inboxes as the original."}
            />
            <RadioOption
                name={"emails"}
                checked={emailStrategy === "PLACEHOLDER"}
                onSelect={() => setEmailStrategy("PLACEHOLDER")}
                label={"Replace them with a placeholder"}
                description={"Use this for a test copy that must not reach the real dealership inboxes."}
            />
            {needsPlaceholderEmail && <div className={styles.field}>
                <CustomInputField
                    isRequired={true}
                    title={"Placeholder email"}
                    value={placeholderEmail}
                    setValue={value => {
                        markTouched("placeholderEmail");
                        setPlaceholderEmail(value);
                    }}
                    placeholder={"placeholder@example.com"}
                    errorMessage={touchedFields.has("placeholderEmail") ? placeholderEmailError : undefined}
                />
            </div>}
        </fieldset>}

        <div className={styles.field}>
            <CustomInputField
                isRequired={true}
                title={"Name prefix"}
                value={namePrefix}
                setValue={value => {
                    markTouched("namePrefix");
                    setNamePrefix(value);
                }}
                placeholder={DEFAULT_NAME_PREFIX}
                errorMessage={touchedFields.has("namePrefix") ? namePrefixError : undefined}
            />
            <span className={styles.hint}>
                Added in front of the name of the assistant{copyHierarchy && ", its franchises and its locations"}.
            </span>
        </div>

        {cloneError && <span className={styles.error} role="alert">{cloneError}</span>}

        <div className={styles.buttonsWrapper}>
            <Button title={"Cancel"} state={ButtonState.SECONDARY} handleClick={handleClose}/>
            <Button
                title={"Clone"}
                state={isValid ? ButtonState.PRIMARY : ButtonState.DISABLED}
                handleClick={handleClone}
            />
        </div>
    </div>
}
